import assert from 'assert';

import { BadRequestError } from '@fragment/error-utils';
import type { VarRecord } from '@fragment/ledger-expressions';
import {
  add,
  evaluateExpressionWithVars,
  subtract,
  toString,
  zero,
} from '@fragment/ledger-expressions';
import type { TypedLedgerEntryLine } from '@fragment/schema-validation/types';
import { stringify } from '@fragment/serde';
import type { LedgerAccountType } from '@fragment/types';
import type { TxType } from '@fragment/types/tx';

import type { LedgerAccount, Line } from './types';
import {
  assertValidCurrency,
  getLineCurrency,
  serializeCurrency,
} from './currency';
import { abs, getCreditOrDebit } from './utils';

export const isPositiveLine = (
  ledgerAccountType: LedgerAccountType,
  type: TxType
): boolean => {
  let normal: TxType | undefined;
  if (ledgerAccountType === 'asset' || ledgerAccountType === 'expense') {
    normal = 'debit';
  } else if (
    ledgerAccountType === 'income' ||
    ledgerAccountType === 'liability'
  ) {
    normal = 'credit';
  }
  return type === normal;
};

type Params = {
  lines: Line[];
  ledgerAccounts: Record<string, LedgerAccount>;
};
export const assertBalancedLines = ({
  lines,
  ledgerAccounts,
}: Params): void => {
  // Both accounts and lines may have a currency defined, so we need to
  // extract the currency from all of them.
  const currencyCarriers = [...Object.values(ledgerAccounts), ...lines];
  const allCurrencies = currencyCarriers.reduce(
    (currencies, { currency, customCurrencyId }) => {
      if (currency && currency !== 'MULTI') {
        currencies.add(serializeCurrency({ currency, customCurrencyId }));
      }
      return currencies;
    },
    new Set<string>()
  );
  const isSingleCurrency = allCurrencies.size === 1;

  // Check assets - liabilities === income - expenses per currency
  allCurrencies.forEach((currentCurrency) => {
    // assets
    let assets = 0n;
    let liabilities = 0n;
    let income = 0n;
    let expenses = 0n;

    const addLineAmountByAccountType = (line: Line) => {
      const ledgerAccountId = line.account.id;
      const ledgerAccount = ledgerAccounts[ledgerAccountId];

      const lineType = getCreditOrDebit(ledgerAccount.type, line);
      const amount = abs(line.amount);

      const isPositive = isPositiveLine(ledgerAccount.type, lineType);

      if (ledgerAccount.type === 'asset') {
        // Debit normal
        if (isPositive) {
          assets += amount;
        } else {
          assets -= amount;
        }
      } else if (ledgerAccount.type === 'liability') {
        // Credit normal
        if (isPositive) {
          liabilities += amount;
        } else {
          liabilities -= amount;
        }
      } else if (ledgerAccount.type === 'expense') {
        // Debit normal
        if (isPositive) {
          expenses += amount;
        } else {
          expenses -= amount;
        }
      } else if (ledgerAccount.type === 'income') {
        // Credit normal
        if (isPositive) {
          income += amount;
        } else {
          income -= amount;
        }
      } else {
        throw new BadRequestError(
          `Got a bad line in ledger entry: ${stringify(line)}`
        );
      }
    };
    const doesLineCurrencyMatch = (line: Line) =>
      serializeCurrency(getLineCurrency({ line, ledgerAccounts })) ===
      currentCurrency;

    lines.filter(doesLineCurrencyMatch).forEach(addLineAmountByAccountType);

    const left = assets - liabilities;
    const right = income - expenses;

    if (left !== right) {
      const errorMessage = isSingleCurrency
        ? 'The ledger entry is not balanced'
        : `The ledger entry is not balanced in currency ${currentCurrency} `;
      throw new BadRequestError(
        {
          code: `unbalanced_ledger_entry`,
        },
        `
        ${errorMessage}:
        assets: ${assets}
        liabilities: ${liabilities}
        income: ${income}
        expenses: ${expenses}

        assets - liabilities: ${left}
        income - expenses: ${right}
        diff: ${left - right}
      `
      );
    }
  });
};

export const assertParameterizedLinesAreBalanced = ({
  lines,
  entryType,
}: {
  lines: (Pick<
    TypedLedgerEntryLine,
    | 'amount'
    | 'tx'
    | 'amountParameters'
    | 'currency'
    | 'parameters'
    | 'accountPath'
  > & { account: Pick<TypedLedgerEntryLine['account'], 'type'> })[];
  entryType: string;
}) => {
  // Both accounts and lines may have a currency defined, so we need to
  // extract the currency from all of them.
  const allCurrencies = lines.reduce((currencies, line) => {
    const { customCurrencyId, code } = line.currency;
    if (code) {
      currencies.add(serializeCurrency({ currency: code, customCurrencyId }));
    }
    return currencies;
  }, new Set<string>());

  allCurrencies.forEach((currentCurrency) => {
    const balances: Record<LedgerAccountType, [bigint, VarRecord]> = {
      asset: zero,
      liability: zero,
      expense: zero,
      income: zero,
    };

    lines
      .filter((line) =>
        line.currency
          ? serializeCurrency({
              currency: line.currency.code,
              customCurrencyId: line.currency.customCurrencyId,
            }) === currentCurrency
          : false
      )
      .forEach((line) => {
        assert(line.account.type);
        if (line.amount) {
          balances[line.account.type] = add(
            balances[line.account.type],
            evaluateExpressionWithVars(line.amount)
          );
        }
      });
    const { asset, liability, expense, income } = balances;
    const wealth = subtract(asset, liability);
    const momentum = subtract(income, expense);
    const diff = subtract(wealth, momentum);
    if (!(diff[0] === 0n && diff[1].size === 0)) {
      throw new BadRequestError(
        `Ledger Entry (${entryType}) is not always balanced in currency ${currentCurrency}:
        assets: ${toString(asset)}
        liabilities: ${toString(liability)}
        income: ${toString(income)}
        expenses: ${toString(expense)}

        assets - liabilities: ${toString(wealth)}
        income - expenses: ${toString(momentum)}
        diff: ${toString(diff)}
        `
      );
    }
  });
};

export const assertBalancedLinesSchemas = <
  L extends Pick<
    TypedLedgerEntryLine,
    | 'amount'
    | 'tx'
    | 'amountParameters'
    | 'currency'
    | 'parameters'
    | 'accountPath'
  > & { account: Pick<TypedLedgerEntryLine['account'], 'type'> }
>({
  lines,
  entryType,
}: {
  lines: L[];
  entryType: string;
}) => {
  // If amount doesn't exist on any line, it is necessarily a 2-line reconciliation entry from earlier validation.
  // This is guaranteed to balance as we populate the amount internally for such entries.
  if (lines.some((line) => !line.amount)) {
    // These assertions are not strictly necessarily but included for safety
    assert(lines.length === 2);
    assert(lines.some((line) => line.tx));
    return;
  }
  const isParameterized = lines.some(
    (line) => line.parameters.length > 0 || line.amountParameters.length > 0
  );

  // If it's not parameterized, just pass-through to assertBalancedLines
  if (!isParameterized) {
    const makeLineId = (line: L) =>
      `${line.accountPath}:${serializeCurrency({
        currency: line.currency.code,
        customCurrencyId: line.currency.customCurrencyId,
      })}`;
    const mappedLines: Line[] = lines.map((line) => {
      return {
        currency: line.currency
          ? assertValidCurrency(line.currency).code
          : undefined,
        customCurrencyId: line.currency?.customCurrencyId,
        amount: BigInt(line.amount!),
        account: {
          id: makeLineId(line),
        },
      };
    });
    const accounts: Record<string, LedgerAccount> = lines.reduce(
      (acc, line) => {
        return {
          ...acc,
          [makeLineId(line)]: {
            type: line.account.type,
            currency: line.currency?.code,
            customCurrencyId: line.currency?.customCurrencyId,
          },
        };
      },
      {}
    );
    assertBalancedLines({
      lines: mappedLines,
      ledgerAccounts: accounts,
    });
  } else {
    assertParameterizedLinesAreBalanced({ lines, entryType });
  }
};
